/**
 * Content Studio — submit social media posts (Twitter, LinkedIn, Instagram,
 * YouTube) for approval via the existing /workflows/ system using kind=
 * 'content_publish'.
 *
 * Tabs:
 *   - Compose       — new draft, with live preview
 *   - In review     — your drafts currently in the approval chain
 *   - Approved      — sign-off complete, ready to post manually
 *   - Posted        — closed-loop entries with the live URL
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, Twitter, Linkedin, Instagram, Youtube, Image as ImageIcon, Link2,
  Send, Upload, Trash2, AtSign, MessageSquareText, ExternalLink,
  Plus, ChevronRight, ListChecks, ShieldCheck, Loader2, Check, Rocket, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import ContentPreviewCard from '@/components/ContentPreviewCard';
import ContentCalendar from '@/components/ContentCalendar';
import { WORKFLOW_STATUS_META } from '@/lib/statusColors';

const PLATFORMS = [
  { id: 'twitter',   label: 'Twitter (X)',  Icon: Twitter,   accent: '#1DA1F2', max: 280  },
  { id: 'linkedin',  label: 'LinkedIn',     Icon: Linkedin,  accent: '#0A66C2', max: 3000 },
  { id: 'instagram', label: 'Instagram',    Icon: Instagram, accent: '#E4405F', max: 2200 },
  { id: 'youtube',   label: 'YouTube',      Icon: Youtube,   accent: '#FF0000', max: 5000 },
];

const MAX_MEDIA_BYTES = 5 * 1024 * 1024; // 5 MB raw → ~7 MB base64

const DEFAULT_STEPS = [
  { step_index: 0, role: 'marketing', label: 'Marketing Lead review',     status: 'pending' },
  { step_index: 1, role: 'ceo',       label: 'CEO / Holding Admin sign-off', status: 'pending' },
];

const ROLE_OPTIONS = [
  { id: 'marketing', label: 'Marketing Lead' },
  { id: 'legal',     label: 'Legal' },
  { id: 'cfo',       label: 'CFO' },
  { id: 'ceo',       label: 'CEO / Holding Admin' },
  { id: 'chairman',  label: 'Chairman' },
];

const STATUS_CLS = (s) => (WORKFLOW_STATUS_META[s]?.cls) || 'bg-slate-100 text-slate-600 border-slate-200';
const STATUS_LABEL = (s) => (WORKFLOW_STATUS_META[s]?.label) || (s || '').replace('_', ' ');

export default function ContentStudio() {
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect */
  const [tab, setTab] = useState('compose');
  const [drafts, setDrafts] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get('/workflows/content');
      setDrafts(data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (tab === 'in_review') return drafts.filter((d) => d.status === 'in_review');
    if (tab === 'approved')  return drafts.filter((d) => d.status === 'approved');
    if (tab === 'posted')    return drafts.filter((d) => d.status === 'executed');
    if (tab === 'rejected')  return drafts.filter((d) => d.status === 'rejected');
    return drafts;
  }, [drafts, tab]);

  return (
    <div className="ve-section" data-testid="content-studio">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow flex items-center gap-2">
            <Sparkles size={14} className="text-[#8B3FC5]"/> Content Studio
          </div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            Every post. <span className="ve-gold-text">Reviewed before it ships.</span>
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Draft tweets, LinkedIn posts, Instagram captions, and YouTube descriptions.
            Each piece routes through a custom approval chain — Marketing Lead, then CEO /
            Holding Admin by default — before anyone marks it posted.
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {[
          { id: 'compose',   label: '+ New draft',  Icon: Plus,         testid: 'cs-tab-compose'   },
          { id: 'all',       label: 'All',          Icon: ListChecks,   testid: 'cs-tab-all'       },
          { id: 'calendar',  label: 'Calendar',     Icon: Calendar,     testid: 'cs-tab-calendar'  },
          { id: 'in_review', label: 'In review',    Icon: Loader2,      testid: 'cs-tab-in-review' },
          { id: 'approved',  label: 'Approved',     Icon: ShieldCheck,  testid: 'cs-tab-approved'  },
          { id: 'posted',    label: 'Posted',       Icon: Check,        testid: 'cs-tab-posted'    },
          { id: 'rejected',  label: 'Rejected',     Icon: Trash2,       testid: 'cs-tab-rejected'  },
        ].map((t) => {
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={t.testid}
                    className={`text-xs px-3 py-1.5 font-bold rounded-md border inline-flex items-center gap-1.5 ${
                      active ? 'bg-[#0A0A0A] text-[#8B3FC5] border-[#0A0A0A]'
                             : 'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5]'
                    }`}>
              <t.Icon size={12}/> {t.label}
              {t.id !== 'compose' && t.id !== 'calendar' && (
                <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] bg-slate-100 text-slate-700">
                  {t.id === 'all' ? drafts.length : drafts.filter((d) => {
                    if (t.id === 'in_review') return d.status === 'in_review';
                    if (t.id === 'approved')  return d.status === 'approved';
                    if (t.id === 'posted')    return d.status === 'executed';
                    if (t.id === 'rejected')  return d.status === 'rejected';
                    return false;
                  }).length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'compose' ? (
        <ComposePanel onSubmitted={() => { load(); setTab('in_review'); }}/>
      ) : tab === 'calendar' ? (
        <ContentCalendar drafts={drafts}/>
      ) : (
        <DraftList items={filtered} onChanged={load}/>
      )}
    </div>
  );
}

/** ───────────── Compose Panel ───────────── */
function ComposePanel({ onSubmitted }) {
  const [platform, setPlatform] = useState('twitter');
  const [body, setBody]         = useState('');
  const [linkUrl, setLinkUrl]   = useState('');
  const [targetAcct, setTarget] = useState('');
  const [scheduledAt, setSched] = useState('');
  const [notes, setNotes]       = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [media, setMedia]       = useState(null);
  const [steps, setSteps]       = useState(DEFAULT_STEPS);
  const [saving, setSaving]     = useState(false);
  const fileRef = useRef(null);

  const meta = PLATFORMS.find((p) => p.id === platform);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) return toast.error('Pick an image.');
    if (f.size > MAX_MEDIA_BYTES) return toast.error('Image must be ≤ 5 MB.');
    const r = new FileReader();
    r.onload = () => setMedia(r.result);
    r.onerror = () => toast.error('Could not read file.');
    r.readAsDataURL(f);
  };

  const updateStep = (idx, key, value) =>
    setSteps((cur) => cur.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  const addStep = () =>
    setSteps((cur) => [...cur, {
      step_index: cur.length, role: 'legal', label: 'Additional review', status: 'pending',
    }]);
  const removeStep = (idx) =>
    setSteps((cur) => cur.length > 1 ? cur.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_index: i })) : cur);

  const charsOk = !body || body.length <= meta.max;
  const canSubmit = body.trim() && charsOk && steps.length >= 1
    && (platform !== 'instagram' || media || videoUrl)
    && (platform !== 'youtube'   || videoUrl || media);

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post('/workflows/content', {
        platform,
        content_type: media ? 'image' : (videoUrl ? 'video' : (linkUrl ? 'link' : 'text')),
        body_text: body.trim(),
        link_url: linkUrl.trim() || null,
        media_data_url: media || null,
        video_url: videoUrl.trim() || null,
        target_account: targetAcct.trim() || null,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
        notes: notes.trim() || null,
        workflow_steps: steps,
      });
      toast.success('Submitted for approval.');
      setBody(''); setLinkUrl(''); setTarget(''); setSched('');
      setNotes(''); setVideoUrl(''); setMedia(null); setSteps(DEFAULT_STEPS);
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed.');
    } finally { setSaving(false); }
  };

  const previewPayload = {
    platform, body_text: body, link_url: linkUrl, target_account: targetAcct,
    scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
    media_data_url: media, video_url: videoUrl, notes,
  };

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* FORM */}
      <div className="lg:col-span-7 ve-card p-6 space-y-5">
        {/* Platform picker */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Platform</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {PLATFORMS.map((p) => {
              const active = p.id === platform;
              return (
                <button key={p.id} onClick={() => setPlatform(p.id)}
                        data-testid={`cs-platform-${p.id}`}
                        className={`px-3 py-3 rounded-md border text-xs font-bold inline-flex items-center justify-center gap-2 transition-all ${
                          active ? 'border-[#8B3FC5] bg-[#FFF8E5]'
                                 : 'border-slate-200 hover:border-[#8B3FC5]/60'
                        }`}>
                  <p.Icon size={14} style={{ color: active ? p.accent : '#94A3B8' }}/>
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold flex items-center gap-1">
              <MessageSquareText size={11} className="text-[#5E1F8B]"/> Post body
            </div>
            <div className={`text-[10px] font-bold ${charsOk ? 'text-slate-500' : 'text-rose-600'}`}>
              {body.length} / {meta.max}
            </div>
          </div>
          <textarea
            value={body} onChange={(e) => setBody(e.target.value)}
            rows={6}
            placeholder={`What's the message? Draft it the way you'd post it on ${meta.label}…`}
            data-testid="cs-body"
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"
          />
        </div>

        {/* Link */}
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-1 flex items-center gap-1">
            <Link2 size={11}/> Link URL <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span>
          </div>
          <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} type="url"
                 placeholder="https://"
                 data-testid="cs-link"
                 className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"/>
        </label>

        {/* Media */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2 flex items-center gap-1">
            <ImageIcon size={11}/> Media
            {(platform === 'instagram' || platform === 'youtube') && (
              <span className="text-rose-600 normal-case tracking-normal font-normal">(required for {meta.label})</span>
            )}
          </div>
          <div className="flex items-start gap-3">
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile}
                   className="hidden" data-testid="cs-media-file"/>
            <button onClick={() => fileRef.current?.click()}
                    data-testid="cs-media-upload"
                    className="ve-btn-secondary text-xs">
              <Upload size={12}/> Upload image
            </button>
            {media && (
              <div className="relative">
                <img src={media} alt="preview" className="h-20 w-20 object-cover rounded border"/>
                <button onClick={() => setMedia(null)}
                        data-testid="cs-media-clear"
                        className="absolute -top-2 -right-2 h-5 w-5 bg-white border border-slate-200 rounded-full flex items-center justify-center text-rose-500">
                  <Trash2 size={10}/>
                </button>
              </div>
            )}
            <input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
                   placeholder="Or paste a video URL"
                   data-testid="cs-video"
                   className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-xs focus:outline-none focus:border-[#8B3FC5]"/>
          </div>
        </div>

        {/* Target + schedule */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-1 flex items-center gap-1">
              <AtSign size={11}/> Target account
            </div>
            <input value={targetAcct} onChange={(e) => setTarget(e.target.value)}
                   placeholder="@vigilantentities"
                   data-testid="cs-target"
                   className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"/>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-1 flex items-center gap-1">
              <Calendar size={11}/> Planned publish time
            </div>
            <input value={scheduledAt} onChange={(e) => setSched(e.target.value)}
                   type="datetime-local"
                   data-testid="cs-schedule"
                   className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"/>
          </label>
        </div>

        {/* Internal notes */}
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">Internal notes for reviewers</div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                    placeholder="Context, asset rights, campaign reference, etc."
                    data-testid="cs-notes"
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5]"/>
        </label>

        {/* Approval chain editor */}
        <div className="border-t border-slate-200 pt-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3 flex items-center justify-between">
            <span className="inline-flex items-center gap-1"><ShieldCheck size={11} className="text-[#5E1F8B]"/> Approval chain</span>
            <button onClick={addStep} className="text-[#5E1F8B] hover:underline" data-testid="cs-step-add">
              + Add step
            </button>
          </div>
          <div className="space-y-2">
            {steps.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-xs"
                   data-testid={`cs-step-${i}`}>
                <span className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600 shrink-0">{i + 1}</span>
                <select value={s.role}
                        onChange={(e) => updateStep(i, 'role', e.target.value)}
                        data-testid={`cs-step-role-${i}`}
                        className="rounded border border-slate-200 px-2 py-1.5 text-xs">
                  {ROLE_OPTIONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
                <input value={s.label}
                       onChange={(e) => updateStep(i, 'label', e.target.value)}
                       data-testid={`cs-step-label-${i}`}
                       className="flex-1 rounded border border-slate-200 px-2 py-1.5 text-xs"/>
                {steps.length > 1 && (
                  <button onClick={() => removeStep(i)} className="text-rose-400 hover:text-rose-600 p-1"
                          data-testid={`cs-step-remove-${i}`}>
                    <Trash2 size={12}/>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-slate-200 pt-5 flex justify-end">
          <button onClick={submit} disabled={!canSubmit || saving}
                  data-testid="cs-submit"
                  className="ve-btn-primary text-sm disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
            Submit for approval <ChevronRight size={12}/>
          </button>
        </div>
      </div>

      {/* PREVIEW */}
      <div className="lg:col-span-5 sticky top-6 self-start">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
          Live preview — what reviewers will see
        </div>
        {body || media || videoUrl ? (
          <ContentPreviewCard payload={previewPayload}/>
        ) : (
          <div className="ve-card p-10 text-center text-sm text-slate-500"
               data-testid="cs-preview-empty">
            Start typing in the post body to see your <b>{meta.label}</b> preview here.
          </div>
        )}
      </div>
    </div>
  );
}

/** ───────────── Draft List ───────────── */
function DraftList({ items, onChanged }) {
  const { user } = useAuth();
  const [postedUrl, setPostedUrl] = useState({});

  if (!items || items.length === 0) {
    return (
      <div className="mt-6 ve-card p-10 text-center text-sm text-slate-500"
           data-testid="cs-empty">
        Nothing here yet.  Drafts you submit will appear in <b>In review</b>;
        approved content shows up in <b>Approved</b> until it&apos;s posted.
      </div>
    );
  }

  const markPosted = async (item) => {
    const url = (postedUrl[item.request_id] || '').trim();
    if (!url) return toast.error('Paste the live post URL.');
    try {
      await api.post(`/workflows/${item.request_id}/mark-posted`, { posted_url: url });
      toast.success('Marked as posted.');
      setPostedUrl((cur) => ({ ...cur, [item.request_id]: '' }));
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };

  const publishNow = async (item) => {
    if (!confirm(`Publish this ${(item.payload || {}).platform || 'post'} immediately to the live platform?`)) return;
    try {
      const { data } = await api.post(`/workflows/${item.request_id}/publish-now`, {});
      const url = (data.payload || {}).posted_url;
      toast.success(`Posted${url ? ' — ' + url : '.'}`);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed.');
    }
  };

  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map((it) => {
        const p = it.payload || {};
        const platMeta = PLATFORMS.find((pp) => pp.id === p.platform) || PLATFORMS[0];
        const isOwner = it.requested_by_user_id === user?.user_id;
        const isAdmin = ['holding_admin', 'system_admin', 'marketing', 'comms_lead'].includes(user?.role);
        const canMark = it.status === 'approved' && (isOwner || isAdmin);
        return (
          <div key={it.request_id} className="ve-card p-5"
               data-testid={`cs-draft-${it.request_id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold"
                   style={{ color: platMeta.accent }}>
                <platMeta.Icon size={12}/> {platMeta.label}
              </div>
              <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded font-bold border ${STATUS_CLS(it.status)}`}>
                {STATUS_LABEL(it.status)}
              </span>
            </div>

            <ContentPreviewCard payload={p}/>

            {/* Step progress */}
            <div className="mt-3 flex items-center gap-1">
              {(it.workflow_steps || []).map((s, i) => (
                <div key={i} className={`flex-1 h-1 rounded-full ${
                  s.status === 'approved' ? 'bg-[#8B3FC5]'
                  : s.status === 'rejected' ? 'bg-rose-400'
                  : 'bg-slate-200'
                }`} title={s.label}/>
              ))}
            </div>

            {/* Open approval detail */}
            <Link to={`/dashboard/approvals/${it.request_id}`}
                  className="ve-btn-ghost text-xs mt-3"
                  data-testid={`cs-draft-open-${it.request_id}`}>
              Open approval thread <ExternalLink size={11}/>
            </Link>

            {/* Mark-posted controls */}
            {canMark && (
              <div className="mt-3 border-t border-slate-200 pt-3 space-y-2">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                  Publish
                </div>
                <button onClick={() => publishNow(it)}
                        data-testid={`cs-publish-now-${it.request_id}`}
                        className="ve-btn-primary text-xs w-full justify-center">
                  <Rocket size={12}/> Publish now via API
                </button>
                <div className="text-[10px] text-slate-400 text-center">— or —</div>
                <div className="flex gap-2">
                  <input
                    value={postedUrl[it.request_id] || ''}
                    onChange={(e) => setPostedUrl((cur) => ({ ...cur, [it.request_id]: e.target.value }))}
                    placeholder="Paste live URL (after manual post)"
                    data-testid={`cs-posted-url-${it.request_id}`}
                    className="flex-1 rounded border border-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:border-[#8B3FC5]"
                  />
                  <button onClick={() => markPosted(it)}
                          data-testid={`cs-mark-posted-${it.request_id}`}
                          className="ve-btn-secondary text-xs">
                    <Check size={11}/> Mark posted
                  </button>
                </div>
              </div>
            )}

            {p.posted_url && (
              <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-1.5">
                <Check size={11} className="text-emerald-500"/>
                Posted at{' '}
                <a href={p.posted_url} target="_blank" rel="noopener noreferrer"
                   className="text-[#5E1F8B] hover:underline break-all">
                  {p.posted_url}
                </a>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
